import {useEffect, useReducer, useRef, useState} from "react";
import styled from "styled-components";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {useAppStore} from "../../../StateManager";
import ArrowDownToBracketIcon from "../icons/arrow-down-to-bracket.svg?react";
import CircleQuestionIcon from "../icons/circle-question.svg?react";
import BugIcon from "../icons/bug.svg?react";
import LightbulbIcon from "../icons/lightbulb-alt.svg?react";
import NotebookIcon from "../icons/notebook.svg?react";
import KeyboardIcon from "../icons/keyboard.svg?react";
import MessageSquareExclamationIcon from "../icons/message-square-exclamation.svg?react";
import {FeatureImportPanelV5} from "../abstract/view/FeatureImportPanelV5";
import {NavigationMenuItem} from "../navigation/NavigationMenuItem";
import {TopBarBackButtonV5} from "../topbar/TopBarBackButtonV5";
import {TimeUtils} from "../../../utils/TimeUtils";
import {ShortcutWindowV5} from "../popup/ShortcutWindowV5";
import {TopBarDropdownV5} from "../dropdown/TopBarDropdownV5";
import {HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {
	NavigationEnum,
	PermissionSetSubNav,
	SettingsModuleNav,
	SettingsModuleSubNav,
	SettingsPermissionsSubNav,
	SettingsSubNav,
} from "../../../Enums";
import {colorPalette} from "../styles/colorPalette";
import {baseDistance, fontSize, fontWeight, radius} from "../styles/styles";
import {useClickOutside, useCloseOnKeyboardPress} from "../utils";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {SettingOptionV5, SettingsLabelContentStyled} from "./SettingOptionV5";
import type {ISettingsSubMenu} from "./SettingsComponentTreeV5";
import {getSettingsComponentById, SettingsComponentTree} from "./SettingsComponentTreeV5";

export const SettingsViewV5 = () => {
	const {appState, setNavBarState} = useAppStore();
	const setModulesTypeAndFieldSearchString = useAppStore((state) => state.setModulesTypeAndFieldSearchString);
	const {
		app: {navigation},
		user,
	} = appState;
	const isUserAdmin = user?.isAdmin;
	const helpRef = useRef<HTMLDivElement>(null);
	const helpPopupRef = useRef<HTMLDivElement>(null);
	const [importOpen, setImportOpen] = useState<boolean>(false);
	const [isShortcutOpen, setIsShortcutOpen] = useState<boolean>(false);
	const [isHelpHovered, setIsHelpHovered] = useState<boolean>(false);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const {selectedMenu, selectedSubMenu, selectedPermissionMenu, selectedSettingsModuleMenu} = appState;
	const ls = appState.app.transport.services.localStorage;

	useEffect(() => {
		if (selectedMenu === NavigationEnum.SETTINGS && !selectedSubMenu) {
			navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [SettingsSubNav.PERSONAL],
			});
		}
	}, [selectedMenu, selectedSubMenu, navigation]);

	useEffect(() => {
		window.addEventListener("hashchange", forceUpdate);

		return () => {
			window.removeEventListener("hashchange", forceUpdate);
		};
	}, []);

	useEffect(() => {
		setNavBarState("hidden");

		return () => {
			if (appState.user?.id) {
				const savedValueForDockedState = ls.get(ls.getV5NavigationPanelDockedStateKey(appState.user.id));

				if (savedValueForDockedState && selectedMenu !== NavigationEnum.SETTINGS) {
					setNavBarState(savedValueForDockedState);
				}
			}
			setNavBarState("closed");
		};
	}, [setNavBarState, ls, appState.user?.id, selectedMenu]);

	useClickOutside([helpRef, helpPopupRef], () => {
		if (isHelpHovered) {
			setIsHelpHovered(false);
		}
	});
	useCloseOnKeyboardPress(() => setIsHelpHovered(false), KeyboardListener.KEY_ESCAPE, [isHelpHovered], {
		enabled: isHelpHovered,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	if (!selectedSubMenu) {
		return <></>;
	}

	const onSelectOption = (
		id: SettingsSubNav | SettingsPermissionsSubNav | SettingsModuleNav,
		parentId: SettingsSubNav | SettingsPermissionsSubNav | SettingsModuleNav | "" = "",
	) => {
		setModulesTypeAndFieldSearchString("");
		if (parentId === SettingsSubNav.MODULES) {
			// #app/<org-id>/settings/modules/xyicon/types
			const parts = StringUtils.decomposeParts(location.hash);
			let settingsPart: SettingsModuleSubNav = SettingsModuleSubNav.TYPES;

			if (parts[5]?.value && parts[4]?.value !== SettingsPermissionsSubNav.PERMISSION_SETS) {
				settingsPart = parts[5]?.value as SettingsModuleSubNav;
			}

			navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [SettingsSubNav.MODULES, id, settingsPart],
			});
		} else if (parentId === SettingsSubNav.PERMISSIONS && id === SettingsPermissionsSubNav.PERMISSION_SETS) {
			// #app/<org-id>/settings/permissions/permissionsets/portfolios
			const parts = StringUtils.decomposeParts(location.hash);
			let settingsPart: PermissionSetSubNav = PermissionSetSubNav.PORTFOLIOS;

			if (parts[5]?.value && parts[4]?.value === SettingsPermissionsSubNav.PERMISSION_SETS) {
				settingsPart = parts[5]?.value as PermissionSetSubNav;
			}

			navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [SettingsSubNav.PERMISSIONS, id, settingsPart],
			});
		} else {
			let activeTab: SettingsModuleSubNav = null;
			const mainSubNav: SettingsSubNav = Object.values(SettingsPermissionsSubNav).includes(id as unknown as SettingsPermissionsSubNav)
				? SettingsSubNav.PERMISSIONS
				: null;

			if (Object.values(SettingsModuleNav).includes(id as unknown as SettingsModuleNav)) {
				activeTab = location.hash.split("/").pop() as SettingsModuleSubNav;

				if (!Object.values(SettingsModuleSubNav).includes(activeTab)) {
					activeTab = SettingsModuleSubNav.TYPES;
				}
			}

			navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [mainSubNav, id, activeTab],
			});
		}
	};

	const settingsNavItems = SettingsComponentTree.flatMap((settingOption, index) => {
		let moduleItems: ISettingsSubMenu[] = [];

		if (settingOption.id === SettingsSubNav.MODULES && settingOption.children) {
			moduleItems.push(...settingOption.children);
		} else {
			moduleItems.push(settingOption);
		}

		return moduleItems;
	});

	const onToggleLiveChatBox = () => {
		const liveChat: HTMLDivElement = document.querySelector("#hubspot-messages-iframe-container.widget-align-left");

		liveChat?.classList.toggle("open");
		setIsHelpHovered(false);
	};

	const onShortCutClicked = async () => {
		if (!isShortcutOpen) {
			setIsShortcutOpen(true);
		} else {
			setIsShortcutOpen(false);
			await TimeUtils.waitForNextFrame();
			setIsShortcutOpen(true);
		}
		setIsHelpHovered(false);
	};

	const onCloseShortcut = () => {
		setIsShortcutOpen(false);
	};

	const onDocsClicked = () => {
		setIsHelpHovered(false);
		appState.app.navigation.openInNewTab("https://support.xyicon.com");
	};

	const onFeatureClicked = () => {
		setIsHelpHovered(false);
		appState.app.navigation.openInNewTab("https://support.xyicon.com/docs/submit-a-feature-request");
	};

	const onBugClicked = () => {
		setIsHelpHovered(false);
		appState.app.navigation.openInNewTab("https://support.xyicon.com/docs/report-an-issue");
	};

	const helpElements = [
		{
			icon: <MessageSquareExclamationIcon />,
			onMouseDown: onToggleLiveChatBox,
			label: "Live Chat",
		},
		{
			icon: <KeyboardIcon />,
			onMouseDown: onShortCutClicked,
			label: "Keyboard Shortcuts",
		},
		{
			icon: <NotebookIcon />,
			onMouseDown: onDocsClicked,
			label: "Help Documents",
		},
		{
			icon: <LightbulbIcon />,
			onMouseDown: onFeatureClicked,
			label: "Suggest a Feature",
		},
		{
			icon: <BugIcon />,
			onMouseDown: onBugClicked,
			label: "Report a Problem",
		},
	];

	const selectedSubMenuIdWithComponent = selectedPermissionMenu || selectedSettingsModuleMenu || selectedSubMenu;

	return (
		<>
			<SettingsContainerStyled>
				<SettingsTabStyled>
					<SettingsBackButtonContainerStyled>
						<TopBarBackButtonV5 />
						<SettingsTitleStyled>SETTINGS</SettingsTitleStyled>
					</SettingsBackButtonContainerStyled>
					<HorizontalSeparatorStyled />
					<NavItemsContainerStyled>
						{settingsNavItems.map((settingOption, index) => {
							if ((!isUserAdmin && ["My Profile", "Organization"].includes(settingOption.label)) || isUserAdmin) {
								return (
									<SettingsContentStyled key={settingOption.id}>
										<SettingOptionV5
											key={settingOption.id}
											label={settingOption.label}
											selected={selectedSubMenuIdWithComponent === settingOption.id}
											onClick={() =>
												onSelectOption(
													settingOption.id,
													Object.values(SettingsModuleNav).includes(settingOption.id as unknown as SettingsModuleNav) ? SettingsSubNav.MODULES : null,
												)
											}
										>
											{settingOption.children && (
												<ChildrenContentStyled>
													{settingOption.children.map((child, index) => (
														<SettingOptionV5
															isHelpHovered={isHelpHovered}
															key={child.id}
															label={child.label}
															selected={selectedSubMenuIdWithComponent === child.id}
															onClick={() => onSelectOption(child.id, settingOption.id)}
														/>
													))}
												</ChildrenContentStyled>
											)}
										</SettingOptionV5>
									</SettingsContentStyled>
								);
							}
						})}
					</NavItemsContainerStyled>
					<WidgetMenuContainerStyled>
						<NavigationMenuItem
							icon={ArrowDownToBracketIcon}
							label="Import"
							onClick={() => setImportOpen(true)}
							small={true}
						/>
						<LeftNavHelpContainerStyled
							ref={helpRef}
							onMouseDown={() => setIsHelpHovered(true)}
						>
							<NavigationMenuItem
								icon={CircleQuestionIcon}
								label="Help"
								onClick={() => setIsHelpHovered(true)}
								small={true}
							/>
						</LeftNavHelpContainerStyled>
						{importOpen && (
							<FeatureImportPanelV5
								onClose={() => setImportOpen(false)}
								dataCyTitle="settings"
							/>
						)}
					</WidgetMenuContainerStyled>
				</SettingsTabStyled>
				<ComponentContainerStyled>{getSettingsComponentById(selectedSubMenuIdWithComponent)}</ComponentContainerStyled>
			</SettingsContainerStyled>
			{isHelpHovered && (
				<LeftNavHelpPopupStyled ref={helpPopupRef}>
					<TopBarDropdownV5
						elements={helpElements}
						parent={helpRef}
						horizontalAlignment={HorizontalAlignment.outerRight}
						verticalAlignment={VerticalAlignment.bottom}
					/>
				</LeftNavHelpPopupStyled>
			)}
			{isShortcutOpen && (
				<DomPortal destination={document.body}>
					<ShortcutWindowV5 onClose={onCloseShortcut} />
				</DomPortal>
			)}
		</>
	);
};

const SettingsContainerStyled = styled.div`
	display: flex;
	flex: 1 1 0px;
	/* min-width: 220px; */
	border-right: 1px solid ${colorPalette.gray.c300};
	position: relative;
	height: 100vh;
`;

const SettingsTabStyled = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 200px;
	padding: ${baseDistance.md};
	border-right: 1px solid ${colorPalette.gray.c300};
	height: 100%;
	gap: ${baseDistance.md};
	position: absolute;
	margin-bottom: 0;
	top: -65px;
	background-color: ${colorPalette.white};
	z-index: 12;

	@media screen and (max-width: 1366px) and (max-height: 768px) {
		overflow-y: auto;
	}
`;

const SettingsContentStyled = styled.div`
	display: flex;
`;

const ComponentContainerStyled = styled.div`
	margin-left: 200px;
	width: 100%;
	display: flex;
`;

const ChildrenContentStyled = styled.div`
	display: flex;
	flex-direction: column;
	text-indent: 16px;
	padding: 0px, 0px, 0px, ${baseDistance.sm};

	${SettingsLabelContentStyled} {
		font-weight: ${fontWeight.normal};
	}
`;

const WidgetMenuContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};
	margin-top: auto;
`;

const SettingsBackButtonContainerStyled = styled.div`
	height: ${baseDistance.md};
	margin-top: ${baseDistance.md};
	display: flex;
	align-items: center;
	gap: ${baseDistance.sm};
`;

const SettingsTitleStyled = styled.div`
	font-size: ${fontSize.md};
	font-weight: ${fontWeight.bold};
	letter-spacing: 0.01em;
`;
const HorizontalSeparatorStyled = styled.div`
	width: 168px;
	height: 2px;
	border-radius: ${radius.sm};
	background-color: ${colorPalette.gray.c100};
`;

const LeftNavHelpContainerStyled = styled.div``;

const LeftNavHelpPopupStyled = styled.div``;

const NavItemsContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};
`;
